import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/devotionalNewArticles.css';
import DevotionalBreadCrumb from './devotionalBreadCrumb.jsx';
import { Helmet } from 'react-helmet-async';
import he from 'he'
import PageAds from '../common/pageAds.jsx';
import { DeviceContext } from './index.jsx';

const LIMIT = 10;

function isTelugu(text) {
  const teluguRegex = /[\u0C00-\u0C7F]/;
  return teluguRegex.test(text);
}

function ArticleSkeleton() {
  return (
    <div className="dna-article-card dna-skeleton-card" aria-hidden="true">
      <div className="dna-card-thumb dna-skeleton-box" />
      <div className="dna-card-body">
        <div className="dna-skeleton-line dna-skeleton-line--title" />
        <div className="dna-skeleton-line dna-skeleton-line--desc" />
        <div className="dna-skeleton-line dna-skeleton-line--desc dna-skeleton-line--short" />
      </div>
    </div>
  );
}

function ArticleCard({ article }) {
  const titleLang = isTelugu(article.content_title) ? 'te' : 'en';

  return (
    <Link
      to={article.content_url ? article.content_url.substring(article.content_url.indexOf('/devotional')+11 ) : '/'}
      className="dna-article-card"
      lang={titleLang}
    >
      <div className="dna-card-thumb">
        <img
          src={article.content_image || '/devotional/assets/images/error_img.webp'}
          alt={article.content_title ? article.content_title : "Title"}
          loading="lazy"
          onError={(e) => {
            e.currentTarget.src = '/devotional/assets/images/error_img.webp';
          }}
        />
      </div>
      <div className="dna-card-body">
        <h2 className="dna-card-title">{article.content_title ? article.content_title : ""}</h2>
        {article.short_desc && (
          <p className="dna-card-desc" dangerouslySetInnerHTML={{__html : article.short_desc ? he.decode(article.short_desc) : ''}} />
        )}
      </div>
    </Link>
  );
}

const DevotionalNewArticles = ({ data: ssrData }) => {

  const initialArticles =
  ssrData?.[0]?.newArticlesPageData?.newArticlesData ?? [];

const isSSRFirstPage = initialArticles.length >= LIMIT;


  const [articles, setArticles] = useState(initialArticles);
const [start, setStart] = useState(
  isSSRFirstPage ? LIMIT : 0
);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
   const {mobile,setMobile,containerRef} = useContext(DeviceContext)
  const sentinelRef = useRef(null);
  const isFetching = useRef(false);

  const fetchArticles = useCallback(async (startIndex) => {

  


    if (isFetching.current) return;
    isFetching.current = true;

    try {
      const res = await fetch(
         `https://www.teluguone.com/devotional_api/DevotionalLatestArticlesJson?start=${startIndex}&limit=${LIMIT}&t=${Date.now()}`
      );
      if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
      const json = await res.json();
      const fetched = json?.home_latest_cat || [];

      if (fetched.length === 0 || fetched.length < LIMIT) {
        setHasMore(false);
      }

      setArticles((prev) => {
        // Deduplicate by content_url
        const existingUrls = new Set(prev.map((a) => a.content_url));
        const newOnes = fetched.filter((a) => !existingUrls.has(a.content_url));
        return [...prev, ...newOnes];
      });
      setStart(startIndex + fetched.length);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      isFetching.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);


useEffect(() => {
  if (!isSSRFirstPage) {
    setLoading(true);
    fetchArticles(0);
  }
}, [isSSRFirstPage, fetchArticles]);

 
  useEffect(() => {
    if (!hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isFetching.current && hasMore) {
          setLoadingMore(true);
          fetchArticles(start);
        }
      },
      { rootMargin: '200px' }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) observer.observe(sentinel);

    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [start, hasMore, fetchArticles]);






  if (loading) {
    return (
      <div className="dna-page">
        <div className="dna-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <ArticleSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (error && articles.length === 0) {
    return (
      <div className="dna-page">
        <div className="dna-error-state">
          <span className="dna-error-icon"></span>
          <p className="dna-error-msg"></p>
          <div className="json-loading-error-div">
               <img   src="/devotional/assets/images/json_error.png" alt="json_error" />
               </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Latest Devotional Articles | TeluguOne</title>
        <meta
          name="description"
          content="Read the latest devotional articles in Telugu — panchangam, horoscopes, spiritual guidance and more."
        />
      </Helmet>

      <div className="dna-page">
        <DevotionalBreadCrumb breadCrumb={"Latest Articles"} />

        <div className="dna-grid">
          {articles && articles.map((article, idx) => (
            <ArticleCard key={`${article.content_url || 'artcile'}-${idx}`} article={article} />
          ))}
        </div>

        {/* Inline error when more items already loaded */}
        {error && articles.length > 0 && (
          <div className="dna-inline-error">
            <p>{error}</p>
            <button
              className="dna-retry-btn"
              onClick={() => {
                setError(null);
                setLoadingMore(true);
                fetchArticles(start);
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Skeleton rows while loading more */}
        {loadingMore && (
          <div className="dna-grid dna-grid--loading">
            {Array.from({ length: 4 }).map((_, i) => (
              <ArticleSkeleton key={`skel-more-${i}`} />
            ))}
          </div>
        )}

        {/* Sentinel div for IntersectionObserver */}
        {hasMore && !error && <div ref={sentinelRef} className="dna-sentinel" aria-hidden="true" />}

        {!hasMore && articles.length > 0 && (
          <p className="dna-end-message">No More</p>
        )}

        <PageAds  mobile={mobile} containerRef={containerRef} />
      </div>
    </>
  );
};

export default DevotionalNewArticles;
